import { useNavigate } from 'react-router-dom';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useContext, useMemo } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { useEntityCreateMutation } from 'app/agriculture/mutations/entity/create/useEntityCreateMutation';
import { InfraType } from 'app/agriculture/mutations/types/entityConfig';
import { AssetIcon } from 'app/agriculture/icons/components';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { InlineAlert } from 'app/agriculture/components/InlineAlert/InlineAlert';
import { InfraSpecificFields } from 'app/agriculture/routes/Infra/components/InfraSpecificFields';
import {
    allGateTypes,
    GateType,
} from 'app/agriculture/queries/entities/types/infrastructure/GateType';
import {
    allBridgeCrossingTypes,
    BridgeCrossingType,
} from 'app/agriculture/queries/entities/types/infrastructure/BridgeCrossingType';
import {
    allPaddockTypes,
    PaddockType,
} from 'app/agriculture/queries/entities/types/infrastructure/PaddockType';
import {
    allVegetationTypes,
    VegetationType,
} from 'app/agriculture/queries/entities/types/infrastructure/VegetationType';
import { MapEntity } from 'app/agriculture/queries/types/geometry/MapEntity';
import {
    allDamTypes,
    DamType,
} from 'app/agriculture/queries/entities/types/infrastructure/DamType';
import {
    allFenceTypes,
    FenceType,
} from 'app/agriculture/queries/entities/types/infrastructure/FenceType';
import { RenderedMapMarker } from 'app/agriculture/components/Map/RenderedMapMarker/RenderedMapMarker';
import { convertGeometryToMapLocation } from 'app/agriculture/components/utils/convertGeometryToLocation/convertGeometryToMapLocation';
import { isBillboardEntityType } from 'app/agriculture/models/Entities';
import { ShapeEditorMapInput } from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorMapInput';
import { getPlaceholderName } from 'app/agriculture/routes/Infra/utils/getPlaceholderName';
import { ContentLayout } from 'app/agriculture/components/ContentLayout/ContentLayout';
import { useEntities } from 'app/agriculture/queries/entities/useEntities';
import { finaliseGeometry } from '../../../../forms/components/ShapeEditor/utils';

interface InfraCreateFormFields {
    name: string;
    geometry?: MapEntity['geometry'];
    bridgeCrossingType: BridgeCrossingType;
    damType: DamType;
    fenceType: FenceType;
    gateType: GateType;
    paddockType: PaddockType;
    vegetationType: VegetationType;
}

interface InfraCreateFormProps<T extends InfraType> {
    infraType: T;
}

export function InfraCreateForm<T extends InfraType>({ infraType }: InfraCreateFormProps<T>) {
    const navigate = useNavigate();
    const routeActions = useRouteActions();
    const { mutate, isPending } = useEntityCreateMutation(infraType);
    const entities = useEntities(infraType);
    const placeholderName = useMemo(
        () => getPlaceholderName({ infraType, entities }),
        [infraType, entities],
    );
    const form = useForm<InfraCreateFormFields>({
        mode: 'onSubmit',
        defaultValues: {
            bridgeCrossingType: allBridgeCrossingTypes[0],
            damType: allDamTypes[0],
            fenceType: allFenceTypes[0],
            gateType: allGateTypes[0],
            paddockType: allPaddockTypes[0],
            vegetationType: allVegetationTypes[0],
        },
        shouldFocusError: true,
    });

    const { agricultureToastService } = useContext(ServicesContext);

    const geometryField = form.register('geometry');

    return (
        <FormProvider {...form}>
            <ContentBox
                title={`Create ${infraType}`}
                Icon={(props) => <AssetIcon {...props} name={infraType} />}
                variant="md"
                forceAsideOpen
                aside={<ShapeEditorMapInput infraType={infraType} />}
            >
                <form
                    onSubmit={form.handleSubmit((fields) => {
                        const geometry =
                            fields.geometry !== undefined
                                ? finaliseGeometry(fields.geometry)
                                : fields.geometry;
                        mutate(
                            {
                                ...fields,
                                geometry,
                                name: fields.name === '' ? placeholderName : fields.name,
                            },
                            {
                                onSuccess: (entity) => {
                                    agricultureToastService
                                        .showToast({
                                            title:
                                                'name' in entity
                                                    ? `${entity.name} has been created successfully`
                                                    : 'Created successfully',
                                            subtext: `The ${infraType} was created!`,
                                            type: AgricultureToastType.SUCCESS,
                                            variant: 'detailed',
                                            actions: [
                                                {
                                                    text: `Add Another ${infraType}`,
                                                    onClick: () => {
                                                        routeActions.create(infraType);
                                                    },
                                                    startIcon: 'add',
                                                },
                                                {
                                                    text: `Edit ${infraType}`,
                                                    onClick: () => {
                                                        routeActions.edit(entity.id, {
                                                            type: infraType,
                                                        });
                                                    },
                                                    startIcon: 'edit',
                                                    variant: 'textonly',
                                                },
                                            ],
                                        })
                                        .catch((e: unknown) => {
                                            console.error(e);
                                        });
                                    form.reset();
                                    routeActions.goto(entity.id, { type: infraType });
                                },
                            },
                        );
                    })}
                >
                    <FormDiscardChangesBlocker />
                    {form.formState.errors.geometry?.message == null ? null : (
                        <ContentBox.Section>
                            <InlineAlert type={AgricultureToastType.ERROR} ref={geometryField.ref}>
                                {form.formState.errors.geometry.message}
                            </InlineAlert>
                        </ContentBox.Section>
                    )}
                    <ContentBox.Section scrollable>
                        <ContentLayout>
                            <Input
                                label="Name"
                                placeholder={placeholderName}
                                {...form.register('name')}
                                errorMessage={form.formState.errors.name?.message}
                            />
                            <InfraSpecificFields infraType={infraType} />
                            {isBillboardEntityType(infraType) ? (
                                <Controller
                                    control={form.control}
                                    name="geometry"
                                    render={({ field }) => {
                                        return (
                                            <RenderedMapMarker
                                                value={
                                                    field.value == null
                                                        ? null
                                                        : convertGeometryToMapLocation(field.value)
                                                }
                                                onChange={({ latitude, longitude }) => {
                                                    field.onChange({
                                                        type: 'Point',
                                                        coordinates: [longitude, latitude],
                                                    });
                                                }}
                                            />
                                        );
                                    }}
                                />
                            ) : null}
                            <Actions
                                sticky
                                primaryAction={{
                                    text: `Create ${infraType}`,
                                    type: 'submit',
                                    isLoading: isPending,
                                }}
                                backAction={{
                                    text: 'Back',
                                    onClick: () => {
                                        navigate(-1);
                                    },
                                }}
                            />
                        </ContentLayout>
                    </ContentBox.Section>
                </form>
            </ContentBox>
        </FormProvider>
    );
}
